'use strict';

const WISHLIST_STORAGE_KEY = 'wishListsArr';

class WishlistButton extends HTMLElement {
  static instances = new Set();
  static ariaLiveEl = null;
  static _announceClearTimer = null;

  constructor() {
    super();

    this.boundToggle = this.toggle.bind(this);
    this.boundHandleCartAdd = this.handleCartAdd.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundSync = this.syncVisualState.bind(this);
  }

  connectedCallback() {
    WishlistButton.instances.add(this);

      this.button = this.querySelector('button');
      if (!this.button) return;

      this.handle = this.button.getAttribute('data-product-handle');
      if (!this.handle) return;

      WishlistButton.setupAriaLiveRegion();

      this.button.addEventListener('click', this.boundToggle);
      this.button.addEventListener('keydown', this.boundHandleKeydown);
      this.button.addEventListener('focus', this.boundSync);
      this.button.addEventListener('blur', this.boundSync);
      this.button.addEventListener('mouseenter', this.boundSync);
      document.addEventListener('cart:item_added', this.boundHandleCartAdd);
      this.syncVisualState();
  }

  disconnectedCallback() {
    WishlistButton.instances.delete(this);

    if (!this.button) return;

    this.button.removeEventListener('click', this.boundToggle);
    this.button.removeEventListener('keydown', this.boundHandleKeydown);
    this.button.removeEventListener('focus', this.boundSync);
    this.button.removeEventListener('blur', this.boundSync);
    this.button.removeEventListener('mouseenter', this.boundSync);
    document.removeEventListener('cart:item_added', this.boundHandleCartAdd);
  }

  static setupAriaLiveRegion() {
    if (WishlistButton.ariaLiveEl && document.body.contains(WishlistButton.ariaLiveEl)) return;

    let ariaLive = document.getElementById('wishlist-aria-live');

    if (!ariaLive) {
      ariaLive = document.createElement('div');
      ariaLive.id = 'wishlist-aria-live';
      ariaLive.setAttribute('aria-live', 'polite');
      ariaLive.setAttribute('aria-atomic', 'true');
      Object.assign(ariaLive.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: '0',
      });
      document.body.appendChild(ariaLive);
    }

    WishlistButton.ariaLiveEl = ariaLive;
  }

  announce(message) {
    WishlistButton.setupAriaLiveRegion();
    const ariaLive = WishlistButton.ariaLiveEl;
    if (!ariaLive) return;

    clearTimeout(WishlistButton._announceClearTimer);
    ariaLive.textContent = '';

    setTimeout(() => {
      ariaLive.textContent = message;

      WishlistButton._announceClearTimer = setTimeout(() => {
        ariaLive.textContent = '';
      }, 3000);
    }, 50);
  }

  static readWishlistFromStorage() {
    try {
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];

      return [...new Set(parsed.filter((handle) => handle != null && String(handle).length))].map(
        (handle) => String(handle)
      );
    } catch (e) {
      console.warn('Invalid wishlist JSON in localStorage', e);
      return [];
    }
  }

  static saveWishlistToStorage(wishlist) {
    try {
      const normalized = Array.isArray(wishlist) ? [...new Set(wishlist)] : [];
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(normalized));
    } catch (e) {
      console.warn('Failed to save wishlist to localStorage', e);
    }
  }

  static getWishlistSet() {
    return new Set(WishlistButton.readWishlistFromStorage());
  }

  isWishlisted(handle) {
    return WishlistButton.getWishlistSet().has(handle);
  }

  getProductId() {
    if (!this.button) return null;
    const id = this.button.getAttribute('data-id');
    return id != null && String(id).length ? String(id) : null;
  }

  updateAriaLabel(isActive) {
    if (!this.button) return;

    const title = this.button.getAttribute('data-title') || '';
    const addText = window.inventory_text?.add_wishlist || 'Add to wishlist';
    const removeText = window.inventory_text?.remove_wishlist || 'Remove from wishlist';
    const label = isActive ? removeText : addText;

    this.button.setAttribute('aria-label', `${label} ${title}`.trim());
    this.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  syncVisualState() {
    if (!this.button || !this.handle) return;

    const isActive = this.isWishlisted(this.handle);

    this.button.classList.toggle('is-active', isActive);
    this.button.classList.toggle('whislist-added', isActive);

    this.updateAriaLabel(isActive);
  }

  dispatchWishlistEvent(action, productId) {
    if (!productId) return;

    const suffix = action === 'added' ? 'added' : 'removed';
    const name = `wishlist:${suffix}:${productId}`;

    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { handle: this.handle, productId },
      })
    );
  }

  static syncAllInstancesForHandle(handle) {
    if (!handle) return;

    for (const instance of WishlistButton.instances) {
      if (instance.handle === handle) instance.syncVisualState();
    }
  }

  notifyThemeWishlistUi(title) {
    if (typeof _theme === 'undefined' || !_theme.Wishlist) return;
    if (typeof _theme.Wishlist.updateWishlistStatus !== 'function') return;

    _theme.Wishlist.updateWishlistStatus(title);
  }

  toggle(event) {
    if (event) event.preventDefault();

    if (!this.button || !this.handle) return;

    const wishlist = WishlistButton.readWishlistFromStorage();
    const set = new Set(wishlist);
    const wasActive = set.has(this.handle);
    const productId = this.getProductId();
    const title = this.button.getAttribute('data-title') || undefined;

    const message = wasActive ? `${title} removed from wishlist` : `${title} added to wishlist`;

    if (wasActive) {
      set.delete(this.handle);
      WishlistButton.saveWishlistToStorage([...set]);
      this.dispatchWishlistEvent('removed', productId);
    } else {
      set.add(this.handle);
      WishlistButton.saveWishlistToStorage([...set]);
      this.dispatchWishlistEvent('added', productId);
    }

    this.announce(message);

    WishlistButton.syncAllInstancesForHandle(this.handle);

    this.notifyThemeWishlistUi(title);
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  handleCartAdd(event) {
    const handleAdded = event.detail?.handle;
    if (handleAdded !== this.handle) return;

    if (!this.isWishlisted(this.handle)) return;

    const set = WishlistButton.getWishlistSet();
    set.delete(this.handle);
    WishlistButton.saveWishlistToStorage([...set]);

    const productId = this.getProductId();
    this.dispatchWishlistEvent('removed', productId);
    WishlistButton.syncAllInstancesForHandle(this.handle);

    this.notifyThemeWishlistUi(this.button?.getAttribute('data-title') || undefined);
    this.announce('Product removed from wishlist because it was added to cart');
  }
}
if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_STORAGE_KEY) return;

  for (const instance of WishlistButton.instances) {
    instance.syncVisualState();
  }

  if (
    typeof _theme !== 'undefined' &&
    _theme.Wishlist &&
    typeof _theme.Wishlist.updateWishlistStatus === 'function'
  ) {
    _theme.Wishlist.updateWishlistStatus();
  }
});
